<template>
  <div :class="['wrapper', shouldFollow ? 'following' : '']">
    <MapItem
      @zoom="handleZoom"
      @pitch="handlePitch"
      @rotate="handleRotate"
      @onLoad="setMap"
      ref="map"
      :map-style="mapStyle"
      :show-controls="false"
      :options="{ center: center, zoom: 9 }"
    >
      <template v-if="route" #default="{ map }">
        <Map3dLayer v-if="!animatingLine" :map="map" />
        <AnimatedMapLine
          :duration="1000"
          @onComplete="lineDrawingDidFinish"
          v-if="animationCount > 0"
          :coordinates="routeAsCoordinates"
          :map="map"
          :draw="animationCount"
          :properties="{
            color: '#08f'
          }"
        />
        <MapCenter
          @onZoomEnd="drawRoute"
          v-if="route && animatingLine"
          :points="routeAsCoordinates"
          :map="map"
        />
        <MapMarker
          :key="`user-${user.id}`"
          :el="marker"
          :draggable="false"
          :map="map"
          :position="markerPosition"
        />
        <template v-for="(marker, i) in markers" :key="i">
          <MapMarker
            @click.stop="marker.onClick"
            :el="marker.element"
            :draggable="false"
            :offset="marker.offset"
            :map="map"
            :position="marker.position"
            :options="marker.options"
          />
        </template>
      </template>
    </MapItem>
  </div>
</template>

<script>
import { lineString, length, along } from '@turf/turf'
import mapboxgl from 'maplibre-gl'

import MapItem from '~/components/map'
import AnimatedMapLine from '~/components/map-animated-line'

import MapMarker from '~/components/map-marker'

const DEFAULT_ZOOM = 13.59
export default {
  name: 'RewindMap',
  components: { MapItem, AnimatedMapLine, MapMarker },
  props: {
    route: {
      type: Object
    },
    userID: {
      type: [String, Number]
    },
    markers: {
      type: Array,
      default: []
    },
    mapStyle: {
      type: String,
      default: 'https://tiles.riserapp.com/styles/RISER-Sat/style.json'
    },
    isPaused: {
      type: Boolean,
      default: false
    },
    isFollowing: {
      type: Boolean,
      default: true
    },
    isSnapped: {
      type: Boolean,
      default: true
    },
    showFullTrip: {
      type: Boolean,
      default: false
    },
    zoomOffset: {
      type: Number,
      default: 5
    },
    pitchOffset: {
      type: Number,

      default: 20
    },
    modelValue: null
  },
  data() {
    const coordinates = this.route.features[0].geometry.coordinates
    return {
      animationCount: ref(0),
      animatingLine: ref(true),
      map: ref(null),
      centerPoint: null,
      user: ref({ id: -1 }),
      markerPosition: ref(coordinates[0]),
      camera: null,
      turfRoute: null,
      lastTimeStamp: null,
      shouldFollow: ref(true),
      progress: ref(this.modelValue),
      bearing: ref(0),
      pitch: ref(0),
      zoom: ref(DEFAULT_ZOOM)
    }
  },
  async created() {
    const { getUser } = useUserService()
    this.user = await getUser(this.userID)
  },
  watch: {
    progress() {
      const lngLat = this.updatePosition(this.progress)
      this.markerPosition = lngLat
      if (this.shouldFollow) {
        this.updateCamera(lngLat)
      }
    },
    async trip() {
      const { getUser } = useUserService()
      this.user = await getUser(this.userID)
    },
    modelValue() {
      this.progress = this.modelValue
    },
    isSnapped() {
      if (this.isSnapped) {
        this.bearing = undefined
        this.pitch = undefined
        this.zoom = undefined
      }
    },
    isFollowing() {
      if (this.isFollowing) {
        this.bearing = undefined
        this.pitch = undefined
        this.zoom = undefined
        this.flyToMarker(this.progress)
        this.map.once('moveend', () => {
          this.shouldFollow = true
        })
      } else {
        this.shouldFollow = false
      }
    },
    showFullTrip() {
      if (this.showFullTrip) {
        this.shouldFollow = false
        const points = this.routeAsCoordinates
        if (points.length > 0) {
          const bounds = points.reduce(
            function (bounds, coord) {
              return bounds.extend(coord)
            },
            new mapboxgl.LngLatBounds(points[0], points[0])
          )
          this.map.fitBounds(bounds, {
            padding: 100,
            pitch: 0
          })
        }
      } else {
        // this.flyToMarker(this.progress)
      }
    }
  },
  computed: {
    center() {
      return this.routeAsCoordinates[0]
    },
    routeAsCoordinates() {
      const coordinates = this.route.features[0].geometry.coordinates
      return coordinates
    },
    marker() {
      const img = document.createElement('img')
      img.src = this.user.profile_photo_thumb_url
        ? this.user.profile_photo_thumb_url
        : '/placeholder/profile_photo.svg'
      img.className = 'user-thumb'
      return img
    }
  },
  methods: {
    setMap(map, centerPoint) {
      this.map = map
      this.centerPoint = centerPoint
    },
    handleRotate(event) {
      if (event.originalEvent) {
        this.$emit('unSnap')
        this.bearing = event.target.getBearing()
      }
    },
    handlePan(event) {
      if (event.originalEvent) {
        this.$emit('unFollow')
      }
    },
    handlePitch(event) {
      if (event.originalEvent) {
        this.$emit('unSnap')
        this.pitch = event.target.getPitch()
      }
    },
    handleZoom(event) {
      if (event.originalEvent) {
        this.$emit('unSnap')
        this.zoom = event.target.getZoom()
      }
    },
    rotateCamera(timestamp) {
      this.map.rotateTo((timestamp / 100) % 360, { duration: 0 })
      requestAnimationFrame(this.rotateCamera)
    },
    lineDrawingDidFinish() {
      this.animatingLine = false
      this.camera = { coord: null, altitude: 75000, distance: null }
      this.turfRoute = lineString(this.routeAsCoordinates)
      const a = mapboxgl.MercatorCoordinate.fromLngLat(
        this.routeAsCoordinates[0],
        this.camera.altitude
      )
      const b = mapboxgl.MercatorCoordinate.fromLngLat(
        along(this.turfRoute, 1).geometry.coordinates,
        this.camera.altitude
      )
      const dx = b.x - a.x
      const dy = b.y - a.y
      this.camera.distance = Math.hypot(dx, dy)
      this.camera.coord = new mapboxgl.MercatorCoordinate(a.x - dx, a.y - dy)
      this.map.once('idle', () => {
        this.flyToMarker(0)
        this.map.once('moveend', () => {
          this.$emit('ready', this.map)
        })
      })
    },
    flyToMarker(progress) {
      const coords = along(this.turfRoute, length(this.turfRoute) * Number.parseFloat(progress))
        .geometry.coordinates
      const params = this.map.calculateCameraOptionsFromTo(
        this.camera.coord.toLngLat(),
        this.camera.altitude,
        coords,
        this.camera.altitude
      )
      params.pitch = this.pitch ? this.pitch : Math.round(params.pitch) - this.pitchOffset
      params.zoom = this.zoom ? this.zoom : DEFAULT_ZOOM
      params.bearing = this.bearing ? this.bearing : params.bearing
      this.map.flyTo(params)
    },
    updatePosition(progress) {
      const coords = along(this.turfRoute, length(this.turfRoute) * Number.parseFloat(progress))
      return coords.geometry.coordinates
    },
    updateCamera(lngLat) {
      // let the camera follow the route
      const coord = mapboxgl.MercatorCoordinate.fromLngLat(lngLat, this.camera.altitude)
      const dx = coord.x - this.camera.coord.x
      const dy = coord.y - this.camera.coord.y
      const delta = Math.hypot(dx, dy) - this.camera.distance
      if (delta > 0) {
        const a = Math.atan2(dy, dx)
        this.camera.coord.x += Math.cos(a) * (delta * 0.01)
        this.camera.coord.y += Math.sin(a) * (delta * 0.01)
      }
      const params = this.map.calculateCameraOptionsFromTo(
        this.camera.coord.toLngLat(),
        this.camera.altitude,
        lngLat,
        this.camera.altitude
      )
      params.pitch = this.pitch ? this.pitch : Math.round(params.pitch) - this.pitchOffset
      params.zoom = this.zoom ? this.zoom + this.zoomOffset : DEFAULT_ZOOM
      params.bearing = this.bearing ? this.bearing : params.bearing
      this.centerPoint(params)
    },

    drawRoute() {
      this.animating = true
      this.animationCount++
    }
  }
}
</script>

<style lang="sass" scoped>

.map
    flex: 1
    font-size: inherit // Stop mapbox from overwriting font size
    background-color: rgb(var(--main-text-color))
    position: relative
    z-index: 0
    height: 100%
    width: 100%

.wrapper
  min-height:  100vh
  min-height: -webkit-fill-available
  width: 100%
</style>
